/* eslint-disable @typescript-eslint/no-explicit-any */

import { find, first, map, sum, trim } from 'es-toolkit/compat';
import { oc } from 'ts-optchain';

import { SharedAppConstants } from '@tock/shared-app-constants';
import { core, safeEnumCompare } from '@tock/coretypes';
import { logger } from '@tock/logging';
import protos from 'utils/protos';

const {
  PaymentCard,
  PaymentCardInsertRequest,
  PaymentCardIntentRequest,
  PaymentProcessorType,
  PurchaseOrder,
} = protos;

interface StripeSetupIntentResponse {
  setupIntent: {
    status: string;
  };
  error: {
    message: string;
  };
}

export interface StripePaymentIntentResponse {
  paymentIntent: {
    status: string;
  };
  error: {
    message: string;
  };
}

interface StripePaymentMethod {
  id: string;
  card: {
    last4: string;
  };
  billing_details: {
    address: {
      postal_code: string;
    };
  };
}

export interface StripeError {
  message?: string;
}

export type StripePaymentMethodResponse =
  | { paymentMethod: StripePaymentMethod; error?: undefined }
  | { paymentMethod?: undefined; error: StripeError };

export function fetchAll(scope?: ApiScope): ApiAction {
  return {
    TockApi: {
      getListProto: '/payment/card',
      type: SharedAppConstants.PAYMENT_CARD_FETCH,
      scope,
    },
  };
}

export function remove(paymentCard: core.PaymentCard): ApiAction {
  return {
    TockApi: {
      deleteProto: `/payment/card/${paymentCard.storedToken}?id=${paymentCard.id}`,
      type: SharedAppConstants.PAYMENT_CARD_DELETE,
      context: { paymentCard },
      onError: {
        3052: '',
        defaultToOriginal: true,
      },
    },
  };
}

function getBraintreeClientToken(): ApiAction {
  return {
    TockApi: {
      getProto: '/payment/braintree/client/token',
      type: SharedAppConstants.PAYMENT_CARD_CLIENT_TOKEN,
    },
  };
}

export function getBraintreeClientInstance(): any {
  return (dispatch: Dispatch) => {
    return new Promise((resolve, reject) => {
      (dispatch(getBraintreeClientToken()) as any).then((response: { payload: string }) => {
        const authorization = response.payload;
        window.braintree.client.create({ authorization }, (error: any, client: any) => {
          if (error) {
            reject(error);
            return;
          }
          resolve(client);
        });
      });
    });
  };
}

export function tokenizeCardWithStripeOnly(paymentCard: NewPaymentCard) {
  return (dispatch: Dispatch) => {
    dispatch({
      type: SharedAppConstants.PAYMENT_CARD_TOKENIZE_STARTING,
    });
    return new Promise((resolve, reject) => {
      (dispatch(stripeValidateAndTokenizeCard(paymentCard)) as any)
        .then((card: any) => {
          if (card == null) {
            logger.error('Could not process payment card', card);
            dispatch({
              type: SharedAppConstants.PAYMENT_CARD_TOKENIZE_FAILED,
              toastMessage: {
                type: 'error',
                message: 'Could not process the payment card',
              },
            });
            reject(new Error('Could not process payment card.'));
            return;
          }

          dispatch({
            type: SharedAppConstants.PAYMENT_CARD_TOKENIZE_DONE,
          });
          const newCard = {
            ...card,
            deviceData: paymentCard.deviceData,
          };
          resolve(newCard);
        })
        .catch((error: any) => {
          logger.error(error);
          (window as any).Rollbar.error(
            `Error tokenizing card: ${error ? JSON.stringify(error) : 'unknown'}`
          );
          dispatch({
            type: SharedAppConstants.PAYMENT_CARD_TOKENIZE_FAILED,
            toastMessage: {
              type: 'error',
                message: (typeof error === 'string') ? error : 'Unable to process the payment card',
            },
          });
          reject(error);
        });
    });
  };
}

export function tokenizeCard(paymentCard: NewPaymentCard) {
  return (dispatch: Dispatch) => {
    dispatch({
      type: SharedAppConstants.PAYMENT_CARD_TOKENIZE_STARTING,
    });
    return new Promise((resolve, reject) => {
      Promise.all([
        dispatch(braintreeValidateAndTokenizeCard(paymentCard)),
        dispatch(stripeValidateAndTokenizeCard(paymentCard)),
      ])
        .then((cards: any) => {
          if (!Array.isArray(cards) || cards.length !== 2) {
            logger.error('Could not process payment card', cards);
            dispatch({
              type: SharedAppConstants.PAYMENT_CARD_TOKENIZE_FAILED,
              toastMessage: {
                type: 'error',
                message: 'Could not process the payment card',
              },
            });
            reject(new Error('Could not process payment card.'));
            return;
          }

          dispatch({
            type: SharedAppConstants.PAYMENT_CARD_TOKENIZE_DONE,
          });
          const newCards = map(cards, (card) => ({
            ...card,
            deviceData: paymentCard.deviceData,
          }));
          resolve(newCards);
        })
        .catch((error: any) => {
          logger.error(error);
          (window as any).Rollbar.error(
            `Error tokenizing card: ${error ? JSON.stringify(error) : 'unknown'}`
          );
          dispatch({
            type: SharedAppConstants.PAYMENT_CARD_TOKENIZE_FAILED,
            toastMessage: {
              type: 'error',
                message: (typeof error === 'string') ? error : 'Unable to process the payment card',
            },
          });
          reject(error);
        });
    });
  };
}

function saveCardWithStripeForward(
  paymentCard: core.PaymentCard,
  options?: SaveCardsToTockOptions
) {
  return (dispatch: Dispatch) => {
    const paymentMethodToken = paymentCard.nonceToken;
    return dispatch(checkPaymentCardPreSaveAction(paymentMethodToken) as any).then(() =>
      dispatch(saveCardToTock(paymentCard, options))
    );
  };
}

function saveCards(paymentCards: core.PaymentCard[], options?: SaveCardsToTockOptions) {
  return (dispatch: Dispatch) => {
    const cardWithPaymentMethodToken = find(paymentCards, (paymentCard) =>
      safeEnumCompare(paymentCard.processorType, PaymentProcessorType, PaymentProcessorType.STRIPE)
    );
    if (!cardWithPaymentMethodToken) {
      // Fallback to just saving the cards. This should never happen.
      (window as any).Rollbar.error(
        `Could not find a payment card with payment method token: ${JSON.stringify(paymentCards)}`
      );
      return dispatch(saveCardsToTock(paymentCards, options));
    }

    const paymentMethodToken = cardWithPaymentMethodToken.nonceToken;
    return dispatch(checkPaymentCardPreSaveAction(paymentMethodToken) as any).then(() =>
      dispatch(saveCardsToTock(paymentCards, options))
    );
  };
}

interface SaveCardsToTockOptions {
  patron?: core.IPatron | null;
  scope?: ApiScope;
  challengeResponse?: string;
  requireTurnstile?: boolean;
  createWithoutPatron?: boolean;
}

export function saveCardToTock(
  paymentCard: core.IPaymentCard,
  options?: SaveCardsToTockOptions
): ApiAction<core.IPaymentCard> {
  const {
    patron,
    scope,
    challengeResponse,
    requireTurnstile = false,
    createWithoutPatron = false,
  } = options || {};
  return {
    TockApi: {
      postProto: '/payment/card',
      request: PaymentCardInsertRequest.fromObject({
        paymentCard: [paymentCard],
        patron,
        challengeResponse,
        requireTurnstile,
        createWithoutPatron,
      }),
      type: SharedAppConstants.PAYMENT_CARD_INSERT,
      scope,
    },
  };
}

export function forwardCardWithoutSaving(
  paymentCard: core.IPaymentCard
): ApiAction<core.IPaymentCard> {
  return {
    TockApi: {
      postProto: '/payment/card/forward',
      request: PaymentCard.fromObject(paymentCard),
      type: SharedAppConstants.PAYMENT_CARD_FORWARD,
    },
  };
}

export function saveCardsToTock(
  paymentCards: core.IPaymentCard[],
  options?: SaveCardsToTockOptions
): ApiAction {
  const {
    patron,
    scope,
    challengeResponse,
    requireTurnstile = false,
    createWithoutPatron = false,
  } = options || {};
  return {
    TockApi: {
      postProto: '/payment/cards',
      request: PaymentCardInsertRequest.fromObject({
        paymentCard: paymentCards,
        patron,
        challengeResponse,
        requireTurnstile,
        createWithoutPatron,
      }),
      type: SharedAppConstants.PAYMENT_CARD_INSERT,
      scope,
    },
  };
}

export function checkPaymentCardPreSaveAction(paymentMethodToken: string) {
  return (dispatch: Dispatch) => {
    return (dispatch(getPotentialSaveCardAction(paymentMethodToken)) as any).then(
      (response: { payload: core.PaymentCardIntentResponse }) => {
        const nextAction = oc(response.payload).scaAction.stripeAction();
        if (!nextAction) {
          // No additional SCA action is necessary, just save the cards.
          return Promise.resolve();
        }

        return window.stripeV3
          .handleCardSetup(nextAction, { payment_method: paymentMethodToken })
          .then((intentResponse: StripeSetupIntentResponse) => {
            if (oc(intentResponse).setupIntent.status() === 'succeeded') {
              return Promise.resolve();
            } else if (intentResponse.error) {
              return Promise.reject(intentResponse.error.message);
            } else {
              (window as any).Rollbar.error(
                `Got unknown response for a SetupIntent from Stripe: ${JSON.stringify(
                  intentResponse
                )}`
              );
              return Promise.reject();
            }
          });
      }
    );
  };
}

export function tokenizeAndSaveCardWithStripeForwarding(
  paymentCard: NewPaymentCard,
  options?: SaveCardsToTockOptions
) {
  return (dispatch: Dispatch) => {
    return (dispatch(tokenizeCardWithStripeOnly(paymentCard)) as any).then(
      (card: core.PaymentCard) => dispatch(saveCardWithStripeForward(card, options))
    );
  };
}

export function tokenizeAndSaveCard(paymentCard: NewPaymentCard, options?: SaveCardsToTockOptions) {
  return (dispatch: Dispatch) => {
    return (dispatch(tokenizeCard(paymentCard)) as any).then((cards: core.PaymentCard[]) =>
      dispatch(saveCards(cards, options))
    );
  };
}

export function braintreeValidateAndTokenizeCard(paymentCard: NewPaymentCard) {
  return (dispatch: Dispatch) => {
    if (paymentCard.nonceToken) {
      return Promise.resolve(paymentCard);
    }
    return (dispatch(getBraintreeClientInstance()) as any)
      .then(
        (client: any) =>
          new Promise((resolve, reject) => {
            client.request(
              {
                endpoint: 'payment_methods/credit_cards',
                method: 'post',
                data: {
                  creditCard: {
                    cardholderName: paymentCard.name,
                    number: paymentCard.number,
                    cvv: paymentCard.cvc,
                    expirationDate: paymentCard.expiry,
                    // If we have a zip, send it otherwise bypass validation
                    // this supports businesses that have AVS disabled
                    ...(paymentCard.zipCode
                      ? {
                          billingAddress: {
                            postalCode: paymentCard.zipCode,
                          },
                        }
                      : {
                          options: {
                            validate: false,
                          },
                        }),
                  },
                },
              },
              (error: any, response: any) => {
                if (error) {
                  const errorMessage = oc(error).details.originalError.error.message(error.message);
                  reject(
                    (typeof errorMessage === 'string') ? `Braintree error: ${errorMessage}` : errorMessage
                  );
                  return;
                }
                resolve({
                  nonceToken: response.creditCards[0].nonce,
                  processorType: PaymentProcessorType.BRAINTREE,
                  brand: oc(response.creditCards[0]).details.cardType(),
                  country: oc(response.creditCards[0]).binData.countryOfIssuance(),
                });
              }
            );
          })
      )
      .catch((error: any) => {
        return Promise.reject(error);
      });
  };
}

export function stripeValidateAndTokenizeCard(paymentCard: NewPaymentCard) {
  return (dispatch: Dispatch) => {
    if (paymentCard.nonceToken) {
      return Promise.resolve(paymentCard);
    }
    try {
      const expiry = paymentCard.expiry.split('/');
      const expMonth = trim(expiry[0]);
      const expYear = trim(expiry[1]);

      return window.stripeV3
        .createPaymentMethod('card', {
          type: 'card',
          card: {
            number: paymentCard.number,
            cvc: paymentCard.cvc,
            exp_month: expMonth,
            exp_year: expYear,
          },
          billing_details: {
            name: paymentCard.name,
            address: {
              postal_code: paymentCard.zipCode || null,
            },
          },
        })
        .then((tokenResponse: StripePaymentMethodResponse) => {
          if (tokenResponse.error) {
            return Promise.reject(tokenResponse.error);
          }

          const paymentMethod = tokenResponse.paymentMethod;
          return {
            nonceToken: paymentMethod.id,
            maskedNumber: paymentMethod.card.last4,
            processorType: PaymentProcessorType.STRIPE,
            zipCode: paymentMethod.billing_details.address.postal_code,
          };
        });
    } catch (error) {
      return Promise.reject((typeof error === 'string') ? `Stripe error: ${error}` : error);
    }
  };
}

export function getChasePayClientToken(): ApiAction {
  return {
    TockApi: {
      getProto: '/payment/chase/client/token',
      type: SharedAppConstants.PAYMENT_CARD_CLIENT_TOKEN,
    },
  };
}

export function collectDeviceData() {
  return (dispatch: Dispatch) => {
    (dispatch(getBraintreeClientInstance()) as any)
      .then((client: any) =>
        window.braintree.dataCollector.create({
          client,
          kount: true,
        })
      )
      .then((dataCollector: any) => {
        dispatch({
          type: SharedAppConstants.PAYMENT_CARD_DEVICE_DATA,
          payload: dataCollector.deviceData,
        });
      });
  };
}

export function getPotentialPrePackagePurchaseAction(
  packageOrder: core.PackagePurchaseOrder
): ApiAction {
  const firstPurchaseOrder = first(packageOrder.purchaseOrder);
  const totalPriceOfPackage = sum(map(packageOrder.purchaseOrder, 'totalPrice'));
  const purchaseOrder = {
    ...firstPurchaseOrder,
    totalPrice: totalPriceOfPackage,
  };
  return {
    TockApi: {
      postProto: '/payment/intent',
      request: PurchaseOrder.fromObject(purchaseOrder),
      type: SharedAppConstants.PAYMENT_CARD_PAYMENT_INTENT,
    },
  };
}

export function getPotentialPrePurchaseAction(
  purchase: core.PurchaseOrder,
  scope?: ApiScope
): ApiAction {
  return {
    TockApi: {
      postProto: '/payment/intent',
      request: PurchaseOrder.fromObject(purchase),
      type: SharedAppConstants.PAYMENT_CARD_PAYMENT_INTENT,
      scope,
    },
  };
}

export function getPotentialSaveCardAction(
  paymentMethodToken: string,
  scope?: ApiScope
): ApiAction {
  return {
    TockApi: {
      postProto: '/payment/card/intent',
      request: PaymentCardIntentRequest.fromObject({ paymentMethodToken }),
      type: SharedAppConstants.PAYMENT_CARD_SETUP_INTENT,
      scope,
    },
  };
}

export function createBraintreeHostedField() {
  return (dispatch: Dispatch) => {
    return new Promise((resolve, reject) =>
      (dispatch(getBraintreeClientInstance()) as any)
        .then((client: any) =>
          window.braintree.hostedFields.create({
            client,
            styles: {
              input: {
                color: '#4f4f65',
                'font-size': '16px',
              },
            },
            fields: {
              cvv: {
                selector: '#cvv',
                placeholder: 'CVC',
              },
            },
          })
        )
        .then((hostedFieldClient: any) => {
          window.braintreeHostedField = hostedFieldClient;
          resolve(hostedFieldClient);
        })
        .catch((error: any) => {
          if (window.LOGGER) {
            window.LOGGER?.warn(
              'Got error hosting CVV revalidation form: ' + JSON.stringify(error)
            );
          }
          reject(error);
        })
    );
  };
}
